import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';

const services = [
  {
    id: 'kitchen',
    title: 'Kitchen Remodeling',
    description: 'Transform your kitchen into a stunning culinary space with custom cabinetry, premium countertops, stylish backsplashes, and thoughtful layouts. We coordinate design and manage every detail of your kitchen renovation.',
    features: ['Custom Cabinet Installation', 'Countertop Selection & Installation', 'Backsplash Design', 'Fixture Upgrades', 'Lighting Updates', 'Flooring Integration'],
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'bathroom',
    title: 'Bathroom Remodeling',
    description: 'Create your personal spa retreat with elegant vanities, quality tile work, luxurious fixtures, and sophisticated finishes. From powder rooms to master baths, we deliver exceptional results.',
    features: ['Vanity & Cabinet Installation', 'Tile Work & Shower Surrounds', 'Fixture Installation', 'Lighting & Mirrors', 'Flooring', 'Accessibility Upgrades'],
    image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'interior',
    title: 'Interior Renovations',
    description: 'Revitalize your living spaces with expert interior renovations that enhance both beauty and functionality. We specialize in non-structural updates that transform the look and feel of your home.',
    features: ['Open Concept Conversions', 'Room Updates', 'Built-in Cabinetry', 'Wall Treatments', 'Interior Reconfigurations', 'Closet Systems'],
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'flooring',
    title: 'Flooring',
    description: 'Elevate your home with premium flooring solutions. From luxury vinyl plank to hardwood and tile, we provide expert installation with meticulous attention to detail.',
    features: ['Luxury Vinyl Plank (LVP)', 'Hardwood Installation', 'Tile Flooring', 'Subfloor Preparation', 'Transitions & Trim', 'Pattern Installations'],
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'tile',
    title: 'Tile Work & Backsplashes',
    description: 'Expert tile installation for floors, walls, showers, and backsplashes. We work with all types of tile to create stunning, lasting results that elevate your space.',
    features: ['Shower Tile Installation', 'Kitchen Backsplashes', 'Floor Tile', 'Wall Tile Applications', 'Accent & Pattern Work', 'Grout & Sealing'],
    image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'painting',
    title: 'Painting Services',
    description: 'Professional interior and exterior painting with premium materials and expert technique. Transform your space with color that lasts beautifully for years.',
    features: ['Interior Painting', 'Exterior Painting', 'Cabinet Refinishing', 'Trim & Detail Work', 'Color Consultation', 'Premium Paint Products'],
    image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'trim',
    title: 'Trim & Finish Carpentry',
    description: 'Elevate your interiors with custom trim work, crown molding, wainscoting, and finish carpentry details that add character and elegance to any room.',
    features: ['Crown Molding', 'Baseboards & Casing', 'Wainscoting & Paneling', 'Built-in Shelving', 'Door Installation', 'Window Trim'],
    image: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'drywall',
    title: 'Drywall Repair & Texture',
    description: 'Expert drywall repair, installation, and texture matching. We restore and refinish walls to seamless perfection, ready for paint or wallcovering.',
    features: ['Hole & Crack Repair', 'Water Damage Repair', 'Texture Matching', 'Smooth Finishes', 'Ceiling Repairs', 'Patch & Prime'],
    image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'doors-windows',
    title: 'Doors & Windows',
    description: 'Upgrade your home with quality door and window installations. We handle interior doors, entry doors, and window replacements where permitted.',
    features: ['Interior Door Installation', 'Entry Door Upgrades', 'Window Replacement', 'Hardware Installation', 'Weather Stripping', 'Trim Integration'],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'deck',
    title: 'Deck Repair & Refinishing',
    description: 'Restore and refinish your outdoor deck to pristine condition. We handle repairs, board replacement, sanding, staining, and sealing.',
    features: ['Board Replacement', 'Structural Repairs', 'Sanding & Prep', 'Staining & Sealing', 'Railing Repairs', 'Power Washing'],
    image: 'https://images.unsplash.com/photo-1591825729269-caeb344f6df2?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'demo',
    title: 'Light Demolition & Haul-Away',
    description: 'Professional demolition services for renovation projects. We handle careful removal and responsible disposal of materials.',
    features: ['Cabinet Removal', 'Flooring Removal', 'Fixture Removal', 'Debris Hauling', 'Site Preparation', 'Clean Demolition'],
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'punch-list',
    title: 'Punch List & Make-Ready',
    description: 'Comprehensive punch list services for homeowners and property managers. We handle the details that make a space move-in ready.',
    features: ['Touch-up Painting', 'Minor Repairs', 'Fixture Adjustments', 'Hardware Tightening', 'Caulking & Sealing', 'Final Details'],
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'rental',
    title: 'Rental Property Refresh',
    description: 'Efficient turnover services for rental properties. We provide cost-effective updates that maximize appeal and minimize vacancy time.',
    features: ['Full Unit Updates', 'Paint Refresh', 'Flooring Updates', 'Fixture Replacement', 'Deep Cleaning Prep', 'Quick Turnaround'],
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80',
  },
];

export const Services = () => {
  return (
    <main data-testid="services-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Our Services</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Comprehensive remodeling and specialty trade services delivered with precision, craftsmanship, and attention to every detail. Serving St. Augustine, Jacksonville, and Northeast Florida.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="w-12 h-px bg-[#C6A87C] mb-6" />
                  <h2 className="font-heading text-2xl md:text-3xl mb-4">{service.title}</h2>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  <ul className="grid grid-cols-2 gap-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 bg-[#C6A87C] rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] hover:text-[#C6A87C] transition-colors"
                  >
                    Request Quote
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Notice */}
      <section className="py-16 bg-[#F9F9F7]" data-testid="compliance-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-gray-600 text-sm leading-relaxed">
            Jax Remodeling Solutions LLC provides remodeling and specialty trade services as permitted. We do not offer services requiring a Florida General Contractor license at this time. Where needed, we can coordinate with properly licensed professionals.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Contact us today for a free consultation and detailed estimate.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:9048636673"
                data-testid="services-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 863-6673
              </a>
              <Link
                to="/contact"
                data-testid="services-estimate-btn"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Request Estimate
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
